"use client"

import { useState } from "react"
import { ChevronDown, Globe, Check } from "lucide-react"
import { useLocale } from "next-intl"
import { usePathname } from "next/navigation"
import { locales } from '@/i18n/config'

interface Language {
  code: string
  name: string
  nativeName: string
  flag: string
}

export default function LanguageSwitcher() {
  const [open, setOpen] = useState(false)
  const locale = useLocale()
  const pathname = usePathname()

  const languages: Language[] = [
    { code: "en", name: "English", nativeName: "English", flag: "🇺🇸" },
    { code: "fr", name: "French", nativeName: "Français", flag: "🇫🇷" },
    { code: "ar", name: "Arabic", nativeName: "العربية", flag: "🇹🇳" },
    { code: "ru", name: "Russian", nativeName: "Русский", flag: "🇷🇺" },
    { code: "zh", name: "Chinese", nativeName: "中文", flag: "🇨🇳" },
    { code: "ja", name: "Japanese", nativeName: "日本語", flag: "🇯🇵" },
    { code: "ko", name: "Korean", nativeName: "한국어", flag: "🇰🇷" },
  ];

  const currentLanguage = languages.find((lang) => lang.code === locale) || languages[0]

  const changeLanguage = (newLocale: string) => {
    const segments = pathname.split('/')
    segments[1] = newLocale
    const newPath = segments.join('/')
    window.location.href = newPath
    setOpen(false)
  }

  return (
    <div className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1.5 sm:gap-2 text-white hover:text-white/90 transition-all bg-white/15 hover:bg-white/25 backdrop-blur-sm px-2.5 sm:px-3 py-1.5 sm:py-2 rounded-lg border border-white/20 hover:border-white/30 min-w-[44px] min-h-[44px] justify-center"
        aria-label="Select language"
      >
        <Globe className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0" />
        <span className="hidden sm:inline text-xs font-medium">{currentLanguage.flag}</span>
        <span className="text-xs sm:text-sm font-semibold uppercase tracking-wide">{currentLanguage.code}</span>
        <ChevronDown className={`w-3 h-3 transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 mt-2 w-72 z-50 bg-white dark:bg-gray-900 text-gray-900 dark:text-white rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className="absolute -top-1 right-4 w-3 h-3 bg-white dark:bg-gray-900 rotate-45 border-l border-t border-gray-200 dark:border-gray-700"></div>
            
            <div className="p-2">
              <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 px-3 py-2 border-b border-gray-100 dark:border-gray-800">
                Select Language
              </div>
              
              <div className="max-h-64 overflow-y-auto py-1">
                {languages.map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => changeLanguage(lang.code)}
                    className={`w-full text-left px-3 py-2.5 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors flex items-center justify-between rounded-lg mx-1 ${
                      locale === lang.code ? "bg-primary/10 text-primary dark:bg-primary/20" : ""
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-lg">{lang.flag}</span>
                      <div className="flex flex-col">
                        <span className="font-medium text-sm">{lang.nativeName}</span>
                        <span className="text-xs text-gray-500 dark:text-gray-400">{lang.name}</span>
                      </div>
                    </div>
                    {locale === lang.code && (
                      <Check className="w-4 h-4 text-primary" />
                    )}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  )
}